import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import AutocompleteField from '~components/FormFields/v2/Autocomplete/AutocompleteField';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { M2MClient } from '~hooks/useApiClients/models';

import { composeMatchKey, isValidMatchKey, MATCH_KEY_MAX_LENGTH } from './matchKeyFormat';

interface AddMatchKeyFormValues {
  source: AutocompleteFieldOption<M2MClient> | null;
  value: string;
}

const EMPTY_FORM_VALUES: AddMatchKeyFormValues = { source: null, value: '' };

export type SourceOptionsStatus = 'error' | 'loading' | 'ready';

interface AddMatchKeyDialogProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onRetryLoadSources: () => void;
  onSubmit: (key: string) => Promise<void>;
  recordName: string;
  sourceOptions: AutocompleteFieldOption<M2MClient>[];
  sourceOptionsStatus: SourceOptionsStatus;
}

export default function AddMatchKeyDialog({
  isOpen,
  isSubmitting,
  onClose,
  onRetryLoadSources,
  onSubmit,
  recordName,
  sourceOptions,
  sourceOptionsStatus,
}: AddMatchKeyDialogProps) {
  const { control, handleSubmit, reset, watch } = useForm<AddMatchKeyFormValues>({
    defaultValues: EMPTY_FORM_VALUES,
  });

  useEffect(() => {
    if (isOpen) {
      reset(EMPTY_FORM_VALUES);
    }
  }, [isOpen, reset]);

  const watchedSource = watch('source');
  const watchedValue = watch('value');
  const selectedSourceSlug = watchedSource?.value ?? '';
  const canSubmit = isValidMatchKey(selectedSourceSlug, watchedValue);
  // The API caps the composed `"{slug}:{value}"` string, so the room left for the
  // value shrinks by the slug and its separator.
  const valueMaxLength = MATCH_KEY_MAX_LENGTH - selectedSourceSlug.length - 1;

  const submitMatchKey = handleSubmit(async ({ source, value }) => {
    if (!source) {
      return;
    }

    await onSubmit(composeMatchKey(source.value, value));
  });

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <DialogHeader onClose={onClose}>
        {t('administration.match_keys.add_match_key_to_record', { recordName })}
      </DialogHeader>

      <DialogContent>
        {sourceOptionsStatus === 'loading' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sourceOptionsStatus === 'error' ? (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 1 }}
          >
            <Typography sx={{ color: 'error.main' }}>
              {t('error_messages.match_keys.failed_to_fetch_sources')}
            </Typography>

            <SmallButton
              color="secondary"
              data-ui="match-keys__add-dialog__retry-sources-button"
              onClick={onRetryLoadSources}
              variant="outlined"
            >
              {t('actions.retry')}
            </SmallButton>
          </Box>
        ) : sourceOptions.length === 0 ? (
          <Typography sx={{ color: 'text.secondary' }}>
            {t('administration.match_keys.no_sources')}
          </Typography>
        ) : (
          <>
            <AutocompleteField
              control={control}
              isRequired
              label={t('administration.match_keys.source')}
              name="source"
              options={sourceOptions}
            />

            <TextInput
              control={control}
              dataUi="match-keys__add-dialog__value-input"
              isRequired
              label={t('administration.match_keys.value')}
              name="value"
              placeholder={t('administration.match_keys.value_placeholder')}
              slotProps={{ htmlInput: { maxLength: valueMaxLength } }}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSubmitting}
          onClick={onClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          data-ui="match-keys__add-dialog__submit-button"
          disabled={!canSubmit}
          loading={isSubmitting}
          onClick={submitMatchKey}
        >
          {t('actions.create')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
